"use client";

import { useEffect, useState } from "react";

export type HydratedTicketDescriptionState = {
  /** Resolved full description (inline or S3), or null while loading / on empty. */
  description: string | null;
  loading: boolean;
  error: string | null;
};

/**
 * Client dual-read for ticket descriptions after UC-TICKET-LONG-NOTES offload.
 * When `descriptionS3Key` is set and inline `description` is empty, fetches
 * `GET /api/tickets/[id]/description` (session cookies).
 */
export function useHydratedTicketDescription(options: {
  ticketId: string | null | undefined;
  inlineDescription?: string | null;
  descriptionS3Key?: string | null;
  /** Fallback while loading / if hydrate fails (usually description_preview). */
  previewSnippet?: string | null;
}): HydratedTicketDescriptionState {
  const ticketId =
    typeof options.ticketId === "string" ? options.ticketId.trim() : "";
  const inline =
    typeof options.inlineDescription === "string"
      ? options.inlineDescription.trim()
      : "";
  const descriptionS3Key =
    typeof options.descriptionS3Key === "string"
      ? options.descriptionS3Key.trim()
      : "";
  const preview =
    typeof options.previewSnippet === "string"
      ? options.previewSnippet.trim()
      : "";

  const needsS3Hydrate = Boolean(ticketId && descriptionS3Key && !inline);

  const [fullDescription, setFullDescription] = useState<string | null>(null);
  const [loading, setLoading] = useState(needsS3Hydrate);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!needsS3Hydrate) {
      setLoading(false);
      setFullDescription(null);
      setError(null);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    void (async () => {
      try {
        const res = await fetch(`/api/tickets/${ticketId}/description`, {
          credentials: "include",
        });
        if (!res.ok) {
          throw new Error(`Failed to load full description (${res.status})`);
        }
        const data = (await res.json()) as { description?: string | null };
        if (!cancelled) {
          setFullDescription(
            typeof data.description === "string" && data.description.trim()
              ? data.description.trim()
              : null
          );
        }
      } catch {
        if (!cancelled) {
          setError("Could not load the full description from storage.");
          setFullDescription(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [ticketId, needsS3Hydrate]);

  const description = inline || fullDescription || preview || null;
  return { description, loading, error };
}
